import React from "react";
import { CloseButton } from "../activity/close-button.component";
import I18 from "../atoms/i18";
import { Loader } from "../loader.component";
import RightSideModal from "../right-side-modal.component";
import SearchBar from "../unsplash-image-picker/unsplash-photo-search-bar";
import SearchGif, { getGifPreviewUrl } from "./tenor-api";
import TenorGifList from "./tenor-gif-list";
interface Props {
  active?: boolean;
  initialSearchQuery: string;
  setActive?: (active: boolean) => void;
  onGifSelect?: (photo: any) => void;
}

/**
 * @description TenorGifPicker lets you search for GIFs (backed by Klipy; Tenor is discontinued).
 * @param {string} initialSearchQuery - The initial search query.
 * @param {boolean} active - Whether the GIF picker is active.
 * @param {function} setActive - Function to set the GIF picker active.
 * @param {function} onGifSelect - Function to call when a GIF is selected.
 */
export default function TenorGifPicker({
  active = false,
  initialSearchQuery = "",
  setActive = (_: boolean) => {},
  onGifSelect = (_: any) => {},
}: Props) {
  const [pics, setPics] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState<number | undefined>();
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [query, setQuery] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [page, setPage] = React.useState("");
  const [convertingToBlob, setConvertingToBlob] = React.useState(false);

  React.useEffect(() => {
    if (!active) {
      return;
    }
    setQuery(initialSearchQuery);
    fetchGif("", initialSearchQuery, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, initialSearchQuery]);

  const fetchGif = (cursor: string, text: string, reset = false) => {
    // A reset is a brand new search, so it has to get through even when the
    // previous query had already been exhausted (hasMore === false) - otherwise
    // the picker stays stuck on the old, empty result set.
    if (isLoading || isLoadingMore || (!hasMore && !reset)) {
      return;
    }
    if (reset || cursor === "") {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    SearchGif(text, cursor, 30, (response) => {
      const newPics = response.results;
      // `next` is an opaque cursor; it comes back empty once the feed runs out
      setPage(response.next);
      setHasMore(Boolean(response.next) && newPics.length > 0);

      if (reset || newPics.length > 0) {
        const mergedPics = reset ? newPics : [...pics, ...newPics];
        setPics(mergedPics);
        setTotal(mergedPics.length);
      }
      setIsLoadingMore(false);
      setIsLoading(false);
    });
  };

  if (!active) {
    return null;
  }

  return (
    <div style={{ zIndex: "auto" }} className="TenorGifPicker">
      <RightSideModal
        active={active}
        setActive={setActive}
        width="540px"
        className="bg-card">
        <div className="h-full">
          <div className="flex place-content-between items-start bg-card px-4 pt-2 text-lg font-bold">
            <I18>Search Gif</I18>
            <CloseButton
              onClick={() => {
                setActive(false);
              }}
            />
          </div>
          <div className="bg-card p-4 shadow">
            <div className="">
              <SearchBar
                placeholder="Search GIFs"
                onSearch={(query: string) => {
                  setPics([]);
                  setHasMore(true);
                  fetchGif("", query, true);
                }}
                query={query}
                setQuery={setQuery}
              />
            </div>
            {/* KLIPY requires visible attribution in any integration that uses
                their API - see partner.klipy.com. Do not remove. */}
            <div className="mt-2 flex justify-end">
              <a
                href="https://klipy.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-secondary-foreground/70 hover:text-secondary-foreground hover:underline">
                Powered by KLIPY
              </a>
            </div>
          </div>

          <TenorGifList
            total={total}
            photoList={pics}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            loadMore={() => {
              // `page` is an opaque cursor - hand it back untouched.
              fetchGif(page, query);
            }}
            onGifSelect={async (gif: any) => {
              try {
                const gifUrl = getGifPreviewUrl(gif);
                if (!gifUrl) {
                  return;
                }
                setConvertingToBlob(true);
                let blob = await fetch(gifUrl).then((r) => r.blob());

                let file = new File([blob], gifUrl.split("/").pop()!, {
                  type: "image/gif",
                });

                setConvertingToBlob(false);
                onGifSelect({
                  ...gif,
                  blobData: file,
                });
              } catch (error) {
                console.log(error);
                setConvertingToBlob(false);
              }
            }}
          />
        </div>
        {isLoading ||
          (convertingToBlob && (
            <div className="absolute bottom-0 left-0 right-0 top-0">
              <div className="flex h-full place-content-center items-center bg-slate-600/70">
                <div className="opacity-100">
                  <Loader />
                </div>
              </div>
            </div>
          ))}
      </RightSideModal>
    </div>
  );
}
